import { z } from 'zod';

const discreteReceiptUnits = new Set([
  'pz',
  'pz.',
  'pezzo',
  'pezzi',
  'nr',
  'nr.',
  'numero',
  'unità',
  'unita',
  'unit',
  'units',
  'pc',
  'pcs',
  'ea',
]);

/**
 * Whether a physical receipt unit represents indivisible pieces.
 *
 * This is intentionally scoped to physical receipt entry. Documentary
 * quantities remain untouched, including historical fractional values.
 */
export function isDiscreteReceiptUnit(unit: string | null | undefined): boolean {
  return typeof unit === 'string' && discreteReceiptUnits.has(unit.trim().toLowerCase());
}

const receiptUuid = z.string().uuid();
const receiptQuantity = z.coerce.number().positive();
const receiptNullableText = z.string().max(255).nullable().optional();
const receiptCalendarDate = z.string().regex(/^(?!0000)\d{4}-\d{2}-\d{2}$/).refine(
  value => !Number.isNaN(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value,
  'Data carico non valida',
);

/**
 * One physical quota may document both its DDT line and PO line.  It is
 * deliberately one row so an invoice or receipt does not count the same goods
 * twice merely because both documents reference them.
 */
export const posReceiptSourceAllocationInputSchema = z.object({
  ddt_passivo_item_id: receiptUuid.optional(),
  purchase_order_item_id: receiptUuid.optional(),
  quantity: receiptQuantity,
}).strict().superRefine((value, ctx) => {
  if (!value.ddt_passivo_item_id && !value.purchase_order_item_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Ogni quota ricevuta deve riferire una riga DDT o una riga ordine.',
      path: ['ddt_passivo_item_id'],
    });
  }
});
export type PosReceiptSourceAllocationInput = z.infer<typeof posReceiptSourceAllocationInputSchema>;

export const posGoodsReceiptFinalizeLineInputSchema = z.object({
  product_id: receiptUuid,
  variant_id: receiptUuid.nullable().optional(),
  /** Documentary supplier identity; internal catalogue SKUs are never valid here. */
  supplier_sku: z.string().trim().min(1).max(255).optional(),
  quantity: receiptQuantity,
  unit_of_measure: z.string().min(1).max(20).optional(),
  unit_price: z.coerce.number().nonnegative().nullable().optional(),
  discount_percent: z.coerce.number().min(0).max(100).nullable().optional(),
  lot_number: receiptNullableText,
  production_date: z.string().max(64).nullable().optional(),
  expiry_date: z.string().max(64).nullable().optional(),
  /** Operator-entered physical receipt note; distinct from document and movement notes. */
  operator_notes: z.string().trim().max(2000).nullable().optional(),
  serial_numbers: z.array(z.string().min(1).max(255)).max(3000).optional(),
  serials: z.array(z.object({
    kind: z.string().min(1).max(64),
    serial_number: z.string().min(1).max(255),
  }).strict()).max(3000).optional(),
  /** This receipt flow accepts exactly one identifier per physical unit. */
  serial_units: z.array(z.object({
    serials: z.array(z.object({
      kind: z.string().min(1).max(64),
      serial_number: z.string().min(1).max(255),
    }).strict()).length(1),
  }).strict()).max(3000).optional(),
  /**
   * Trusted acceptance: the serials of this line were taken from the DDT
   * declaration without scanning. Server-gated by a dedicated permission and
   * accepted only when every serial is declared on the allocated DDT line.
   */
  declared_serials_trusted: z.boolean().optional(),
  // Empty only for the explicit root-level manual_receipt mode. Root
  // validation below keeps documentary receipts provenance-complete.
  allocations: z.array(posReceiptSourceAllocationInputSchema).default([]),
}).strict().superRefine((value, ctx) => {
  if (value.declared_serials_trusted && !value.allocations.some(allocation => allocation.ddt_passivo_item_id)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'L’accredito fiduciario richiede una riga DDT che dichiari i seriali.', path: ['declared_serials_trusted'] });
  }
  const representations = [value.serial_units, value.serials, value.serial_numbers].filter(entry => entry !== undefined);
  if (representations.length > 1) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Fornire una sola rappresentazione dei seriali verificati.', path: ['serial_units'] });
  }
  const identifiers = value.serial_units?.flatMap(unit => unit.serials) ?? value.serials
    ?? value.serial_numbers?.map(serial_number => ({ kind: 'serial', serial_number })) ?? [];
  if (new Set(identifiers.map(serial => serial.kind)).size > 1) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'La riga richiede un solo tipo di seriale.', path: ['serial_units'] });
  }
  if (isDiscreteReceiptUnit(value.unit_of_measure)) {
    if (!Number.isInteger(value.quantity)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'La quantità fisicamente ricevuta deve essere intera per questa unità.',
        path: ['quantity'],
      });
    }
    value.allocations.forEach((allocation, allocationIndex) => {
      if (!Number.isInteger(allocation.quantity)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'La quota fisica deve essere intera per questa unità.',
          path: ['allocations', allocationIndex, 'quantity'],
        });
      }
    });
  }
  if (value.allocations.length === 0) return;
  const allocated = value.allocations.reduce((sum, allocation) => sum + allocation.quantity, 0);
  if (Math.abs(allocated - value.quantity) > 0.0001) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'La somma delle quote deve coincidere con la quantità fisicamente ricevuta.',
      path: ['allocations'],
    });
  }
});
export type PosGoodsReceiptFinalizeLineInput = z.infer<typeof posGoodsReceiptFinalizeLineInputSchema>;

export const posGoodsReceiptOverreceiptSchema = z.object({
  authorized: z.literal(true),
  reason: z.string().trim().min(3).max(2000),
}).strict();
export type PosGoodsReceiptOverreceipt = z.infer<typeof posGoodsReceiptOverreceiptSchema>;

/**
 * The operation ID belongs to the physical finalization, never to a DDT or
 * purchase order. A retry must reuse it; a later progressive receipt must use
 * a different ID.
 */
/** Typed conflict code for a repeated identifier inside one physical receipt. */
export const RECEIPT_SERIAL_DUPLICATE_CODE = 'receipt_serial_duplicate';

export const posGoodsReceiptFinalizeInputSchema = z.object({
  operation_id: receiptUuid,
  /** Effective date; omitted by older clients means server CURRENT_DATE on first finalization only. */
  receipt_date: receiptCalendarDate.optional(),
  warehouse_id: receiptUuid.optional(),
  /** Required for manual receipts; documentary receipts derive it from their source rows. */
  supplier_id: receiptUuid.optional(),
  lines: z.array(posGoodsReceiptFinalizeLineInputSchema).min(1).max(1000),
  /**
   * A physical counter/manual receipt has no invented DDT or PO provenance.
   * It is an explicit auditable mode, separately permission-gated server-side.
   */
  manual_receipt: z.object({
    reason: z.string().trim().min(3).max(2000),
  }).strict().optional(),
  overreceipt: posGoodsReceiptOverreceiptSchema.optional(),
}).strict().superRefine((value, ctx) => {
  // This command is one physical receipt: repeated products/allocations must
  // not allow the same supplied identifier to enter on separate lines.
  // Keep this policy local; the general physical unit engine is unchanged.
  const receiptSerialValues = new Set<string>();
  value.lines.forEach((line, lineIndex) => {
    const identifiers = line.serial_units?.flatMap(unit => unit.serials) ?? line.serials
      ?? line.serial_numbers?.map(serial_number => ({ kind: 'serial', serial_number })) ?? [];
    identifiers.forEach((serial, serialIndex) => {
      const rawKey = serial.serial_number.trim().toLowerCase();
      const physicalKey = serial.kind === 'macaddr' ? rawKey.replace(/[^0-9a-f]/g, '')
        : serial.kind === 'imei' || serial.kind === 'iccid' ? rawKey.replace(/[^0-9]/g, '') : rawKey;
      if (receiptSerialValues.has(rawKey) || receiptSerialValues.has(physicalKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Identificativo duplicato nel carico, anche tra righe diverse.',
          path: ['lines', lineIndex, line.serial_units ? 'serial_units' : line.serials ? 'serials' : 'serial_numbers', serialIndex],
          // Domain code: service callers (HTTP, workflow, MCP) must receive the
          // typed conflict, not an anonymous schema failure.
          params: { code: RECEIPT_SERIAL_DUPLICATE_CODE },
        });
      }
      receiptSerialValues.add(rawKey);
      receiptSerialValues.add(physicalKey);
    });
  });
  const hasDocumentaryQuota = value.lines.some((line) => line.allocations.length > 0);
  if (value.manual_receipt && hasDocumentaryQuota) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Un ricevimento manuale non può dichiarare quote DDT/ordine.', path: ['lines'] });
  }
  if (!value.manual_receipt && value.lines.some((line) => line.allocations.length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Ogni riga di un ricevimento documentale richiede quote DDT o ordine.',
      path: ['lines'],
    });
  }
  if (value.manual_receipt && !value.warehouse_id) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Il ricevimento manuale richiede un magazzino esplicito.', path: ['warehouse_id'] });
  }
  if (value.manual_receipt && !value.supplier_id) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Il ricevimento manuale richiede un fornitore esplicito.', path: ['supplier_id'] });
  }
  if (value.manual_receipt && value.overreceipt) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Il sovraricevimento è applicabile solo a una quota ordine.', path: ['overreceipt'] });
  }
});
export type PosGoodsReceiptFinalizeInput = z.infer<typeof posGoodsReceiptFinalizeInputSchema>;

export const posGoodsReceiptFinalizeResultSchema = z.object({
  receipt_id: receiptUuid,
  operation_id: receiptUuid,
  idempotent_replay: z.boolean(),
  status: z.literal('finalized'),
  inserted_movements_count: z.number().int().nonnegative(),
  units_in_stock_count: z.number().nonnegative(),
});
export type PosGoodsReceiptFinalizeResult = z.infer<typeof posGoodsReceiptFinalizeResultSchema>;

/**
 * Read DTOs for the immutable physical-receipt ledger.  These are intentionally
 * separate from the finalize/reassignment inputs: database identities and
 * derived allocation quantities must never be accepted as write input.
 */
export const posGoodsReceiptAllocationReadSchema = z.object({
  id: receiptUuid,
  ddt_passivo_item_id: receiptUuid.nullable(),
  purchase_order_item_id: receiptUuid.nullable(),
  quantity_accepted: z.coerce.number().positive(),
  // Derived server-side from canonical physical-slot invoice rows and the
  // exact legacy receipt/source vector fallback. It is deliberately required:
  // clients must fail closed rather than treating an old/incomplete response
  // as an uninvoiced physical slot that can be removed.
  invoiced_quantity: z.coerce.number().nonnegative(),
}).strict();
export type PosGoodsReceiptAllocationRead = z.infer<typeof posGoodsReceiptAllocationReadSchema>;

export const posGoodsReceiptLineReadSchema = z.object({
  id: receiptUuid,
  product_id: receiptUuid,
  /** Anagrafica corrente per la lettura (deep-link avvisi); opzionale per producer meno recenti. */
  product_name: z.string().nullable().optional(),
  sku: z.string().nullable().optional(),
  variant_id: receiptUuid.nullable(),
  lot_id: receiptUuid.nullable(),
  stock_movement_id: receiptUuid.nullable(),
  quantity: z.coerce.number().positive(),
  // Residual physical quantity with no current documentary allocation.  It is
  // deliberately a line fact, not a synthetic allocation row: reassignment
  // must distinguish this residual from every persisted allocation UUID.
  unassigned_quantity: z.coerce.number().nonnegative(),
  unit_cost: z.coerce.number().nonnegative().nullable(),
  unit_of_measure: z.string().nullable(),
  operator_notes: z.string().nullable(),
  allocations: z.array(posGoodsReceiptAllocationReadSchema),
}).strict();
export type PosGoodsReceiptLineRead = z.infer<typeof posGoodsReceiptLineReadSchema>;

export const posGoodsReceiptReadSchema = z.object({
  id: receiptUuid,
  operation_id: receiptUuid,
  company_id: receiptUuid,
  warehouse_id: receiptUuid,
  supplier_id: receiptUuid.nullable(),
  purchase_order_id: receiptUuid.nullable(),
  ddt_passivo_id: receiptUuid.nullable(),
  status: z.literal('finalized'),
  overreceipt_authorized: z.boolean(),
  overreceipt_reason: z.string().nullable(),
  /** Original autonomous-receipt motivation; unknown for historical receipts. */
  manual_reason: z.string().trim().min(3).max(2000).nullable(),
  /** Unknown for historical receipts; optional for older response producers. */
  receipt_date: receiptCalendarDate.nullable().optional(),
  finalized_by: receiptUuid.nullable(),
  finalized_at: z.string(),
  lines: z.array(posGoodsReceiptLineReadSchema),
}).strict();
export type PosGoodsReceiptRead = z.infer<typeof posGoodsReceiptReadSchema>;

export const posGoodsReceiptListReadSchema = z.object({
  items: z.array(posGoodsReceiptReadSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
}).strict();
export type PosGoodsReceiptListRead = z.infer<typeof posGoodsReceiptListReadSchema>;

export const posDdtPassivoOrderAllocationInputSchema = z.object({
  ddt_passivo_item_id: receiptUuid,
  purchase_order_item_id: receiptUuid,
  quantity_declared: receiptQuantity,
}).strict();
export type PosDdtPassivoOrderAllocationInput = z.infer<typeof posDdtPassivoOrderAllocationInputSchema>;

/** Read shape deliberately differs from strict replacement input. */
export const posDdtPassivoOrderAllocationReadSchema = z.object({
  id: receiptUuid,
  ddt_passivo_item_id: receiptUuid,
  purchase_order_item_id: receiptUuid,
  quantity_declared: z.coerce.number().positive(),
  quantity_received: z.coerce.number().nonnegative(),
  quantity_remaining: z.coerce.number().nonnegative(),
  created_at: z.string(),
}).strict();
export type PosDdtPassivoOrderAllocationRead = z.infer<typeof posDdtPassivoOrderAllocationReadSchema>;

/**
 * Documentary reassignment of an already-finalized physical quota. Receipt-line
 * identity is absent because it is server-owned; an existing slot may carry a
 * replacement quantity when an immutable physical total is split into new
 * documentary slots.
 */
export const posReceiptLineAllocationReassignmentSchema = z.object({
  /** Omit for a new documentary sub-quota created by splitting a slot. */
  id: receiptUuid.optional(),
  ddt_passivo_item_id: receiptUuid.nullable().optional(),
  purchase_order_item_id: receiptUuid.nullable().optional(),
  /** Existing slots retain their quantity when present; omitted slots are removed. */
  quantity: receiptQuantity.optional(),
}).strict().superRefine((value, ctx) => {
  if (!value.ddt_passivo_item_id && !value.purchase_order_item_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'La quota fisica deve mantenere almeno una sorgente documentale.',
      path: ['ddt_passivo_item_id'],
    });
  }
  if (!value.id && value.quantity === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Una nuova quota richiede quantità positiva.', path: ['quantity'] });
  }
});
export type PosReceiptLineAllocationReassignment = z.infer<typeof posReceiptLineAllocationReassignmentSchema>;